/**
 * The in-play HUD (RC2-07) — the status strip and the action hotbar, as React
 * islands rendered from `@annwn/ui`.
 *
 * This is the chrome the player stares at for ninety minutes, and it is the last
 * of the hand-rolled surfaces. The strip was `status-strip.ts`, a `<ul>` of
 * `<li>` chips and a `<p>`; it is now `Meter`s and `StatusTag`s, so the body
 * readouts on the strip, in the condition panel and on the character sheet are
 * one component with one set of rules rather than three that agree by hand.
 *
 * ## The hotbar is composed, not promoted
 *
 * The ticket's open question. The reference's bottom row is built here from
 * `Button` and `Badge` rather than promoted to a library `HotBar`, because the
 * portal has no keyboard-driven bar and nothing else in the game wants one: a
 * component with a single call site is a call site with extra indirection. The
 * moment a second surface needs it, `HudSlot` is already the props object it
 * would take, and the promotion is a move rather than a rewrite. *(Recorded on
 * the ticket.)*
 *
 * ## Why these are islands and not one component
 *
 * The strip is at the top of the overlay and the bar at the bottom, with the
 * canvas between them; they are two roots so the shell can lay them out with
 * ordinary CSS instead of a full-height flex wrapper that would have to sit over
 * the canvas and then carefully hand every click back to it.
 *
 * Both receive already-derived view models and emit one intent each. Neither
 * touches a socket, and neither holds game state.
 */

import { createRoot, type Root } from 'react-dom/client';

import type { SelfSnapshot, SkySnapshot } from '@annwn/protocol';
import { Badge, Button, Meter, StatusTag } from '@annwn/ui';

import {
  hudClock,
  hudMeters,
  hudObjective,
  hudProse,
  hudSlots,
  hudTags,
  hudWho,
  type HudSlot,
  type HudState,
} from './hud-view-model.js';

/* -------------------------------------------------------------------------- *
 * The status strip
 * -------------------------------------------------------------------------- */

interface StripProps {
  readonly self: SelfSnapshot | null;
  readonly sky: SkySnapshot | null;
  /** Connection trouble, or `''`. The one non-diegetic thing the strip shows. */
  readonly status: string;
}

function StatusStripPanel({ self, sky, status }: StripProps) {
  const who = hudWho(self);
  const meters = hudMeters(self?.combat ?? null);
  const tags = hudTags(self);
  const clock = hudClock(sky);
  const prose = hudProse(self?.combat ?? null);
  const objective = hudObjective(self);

  return (
    <div className="hud-strip">
      {who ? (
        <div className="hud-who">
          <b className="hud-who__name">{who.name}</b>
          {who.line ? <span className="hud-who__line">{who.line}</span> : null}
        </div>
      ) : null}

      <div className="hud-meters">
        {meters.map((meter) => (
          <Meter
            className="hud-meter"
            displayValue={meter.displayValue}
            key={meter.id}
            label={meter.label}
            max={meter.max}
            segments={14}
            value={meter.value}
            variant={meter.variant}
          />
        ))}
      </div>

      {objective ? <p className="hud-objective">{objective}</p> : null}

      {/* The chips and the sentence under them are one column: the chips are
          what is true this second, the sentence is how the last minute has
          gone, and reading them together is the whole point of the strip. */}
      <div className="hud-state">
        {tags.length > 0 ? (
          <div className="hud-tags">
            {tags.map((tag) => (
              <StatusTag key={tag.id} tone={tag.tone}>
                {tag.label}
              </StatusTag>
            ))}
          </div>
        ) : null}
        {prose ? (
          <p aria-live="polite" className="hud-prose">
            {prose}
          </p>
        ) : null}
      </div>

      {status ? (
        <p className="hud-alert" role="status">
          {status}
        </p>
      ) : null}

      <span className="hud-compass" aria-label="North is up on the map">
        N ↑
      </span>

      {clock ? (
        <div className={clock.lit ? 'hud-clock hud-clock--lit' : 'hud-clock'}>
          <b className="hud-clock__time">{clock.time}</b>
          <span className="hud-clock__weather">{clock.weather}</span>
          <span className="hud-clock__ground">{clock.ground}</span>
          {clock.warning ? <span className="hud-clock__warning">{clock.warning}</span> : null}
        </div>
      ) : null}
    </div>
  );
}

export interface StatusStripHandle {
  update(self: SelfSnapshot | null, sky: SkySnapshot | null, status: string): void;
  destroy(): void;
}

export function mountStatusStrip(container: HTMLElement): StatusStripHandle {
  const root: Root = createRoot(container);
  let self: SelfSnapshot | null = null;
  let sky: SkySnapshot | null = null;
  let status = '';

  const render = (): void => {
    root.render(<StatusStripPanel self={self} sky={sky} status={status} />);
  };

  render();

  return {
    update(nextSelf, nextSky, nextStatus) {
      self = nextSelf;
      sky = nextSky;
      status = nextStatus;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}

/* -------------------------------------------------------------------------- *
 * The hotbar
 * -------------------------------------------------------------------------- */

export interface HotBarOptions {
  /** A slot was clicked — its id is a keymap `Action.kind`. */
  onSlot(id: string): void;
}

interface HotBarProps extends HotBarOptions {
  readonly self: SelfSnapshot | null;
  readonly state: HudState;
}

/**
 * One slot: the key you would press, what it does, and one clause of why not.
 *
 * A `Button` rather than a `div`, because it is one — the bar has to be usable
 * with a mouse, and a clickable div is a control screen readers cannot find and
 * a keyboard cannot reach. `tabIndex={-1}` keeps it out of the Tab order all the
 * same: Tab belongs to the overlays, and a player tabbing through eleven slots
 * to reach the chat line would be the focus handoff failing in a new way.
 */
function Slot({ slot, onSlot }: { slot: HudSlot; onSlot(id: string): void }) {
  return (
    <Button
      aria-pressed={slot.on ?? undefined}
      className={slot.on ? 'hud-slot hud-slot--on' : 'hud-slot'}
      disabled={slot.disabled ?? false}
      onClick={() => onSlot(slot.id)}
      tabIndex={-1}
      title={
        slot.meta ? `${slot.label} (${slot.key}) — ${slot.meta}` : `${slot.label} (${slot.key})`
      }
      type="button"
      variant="ghost"
    >
      <Badge className="hud-slot__key" tone={slot.on ? 'life' : 'neutral'}>
        {slot.key}
      </Badge>
      <span className="hud-slot__label">{slot.label}</span>
      {slot.meta ? <em className="hud-slot__meta">{slot.meta}</em> : null}
    </Button>
  );
}

function HotBarPanel({ self, state, onSlot }: HotBarProps) {
  const slots = hudSlots(self, state);
  const verbs = slots.filter((slot) => slot.group === 'verb');
  const panels = slots.filter((slot) => slot.group === 'panel');

  return (
    <div aria-label="Actions" className="hud-bar" role="toolbar">
      <div className="hud-bar__group">
        {verbs.map((slot) => (
          <Slot key={slot.id} onSlot={onSlot} slot={slot} />
        ))}
      </div>
      <div className="hud-bar__group hud-bar__group--panels">
        {panels.map((slot) => (
          <Slot key={slot.id} onSlot={onSlot} slot={slot} />
        ))}
      </div>
    </div>
  );
}

export interface HotBarHandle {
  update(self: SelfSnapshot | null, state: HudState): void;
  destroy(): void;
}

export function mountHotBar(container: HTMLElement, options: HotBarOptions): HotBarHandle {
  const root: Root = createRoot(container);
  let self: SelfSnapshot | null = null;
  let state: HudState = {};

  const render = (): void => {
    root.render(<HotBarPanel onSlot={options.onSlot} self={self} state={state} />);
  };

  render();

  return {
    update(nextSelf, nextState) {
      self = nextSelf;
      state = nextState;
      render();
    },
    destroy() {
      root.unmount();
    },
  };
}
